import React, { useState } from 'react';
import { Modal, Select } from 'antd';
import { useAntdAppMessage } from '../../hooks/useAntdAppMessage';
import { CloseOutlined } from '@ant-design/icons';
import CustomButton from '../Button';
import styles from './CompareModal.module.scss';
import { formatVersionSelectorLabel } from '../../utils/versionDisplayName';

interface CompareModalProps {
  visible: boolean;
  onCancel: () => void;
  onConfirm: (selectedVersionId: number) => void;
  loading: boolean;
  fileDetail?: any;
  selectedVersion?: number;
}

const CompareModal: React.FC<CompareModalProps> = ({
  visible,
  onCancel,
  onConfirm,
  loading,
  fileDetail,
  selectedVersion
}) => {
  const message = useAntdAppMessage();
  const [selectedVersionForCompare, setSelectedVersionForCompare] = useState<number | null>(null);

  const getVersionOptions = () => {
    if (!fileDetail?.versions) return [];
    const currentSelectedVersion = fileDetail.versions.find((v: any) => v.id === selectedVersion)?.id;
    return fileDetail.versions
      .filter((version: any) => version.id !== currentSelectedVersion)
      .map((version: any) => ({
        label: formatVersionSelectorLabel(version.name) || `Version ${version.id}`,
        value: version.id,
        disabled: false
      }));
  };

  const handleConfirm = () => {
    if (!selectedVersionForCompare) {
      message.error('Please select a version');
      return;
    }
    onConfirm(selectedVersionForCompare);
    setSelectedVersionForCompare(null);
  };

  const handleCancel = () => {
    onCancel();
    setSelectedVersionForCompare(null);
  };

  return (
    <Modal
      centered
      title={
        <div className={styles.modalHeader}>
          <span className={styles.modalTitle}>Compare with</span>
          <CloseOutlined onClick={handleCancel} className={styles.closeIcon} />
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      className={styles.compareModal}
      width={600}
    >
      <div className={styles.modalContent}>
        <div className={styles.content}>
          <div className={styles.selectRow}>
            <p className={styles.helpText}>
              Please select a version to compare with the current version.
            </p>
            <Select
              className={styles.versionSelect}
              placeholder="Versions"
              value={selectedVersionForCompare || undefined}
              onChange={setSelectedVersionForCompare}
              loading={false}
              options={getVersionOptions()}
            />
          </div>
        </div>
        <div className={styles.actionButtons}>
          <CustomButton onClick={handleCancel} className={styles.cancelButton}>
            Cancel
          </CustomButton>
          <CustomButton
            onClick={handleConfirm}
            loading={loading}
            className={styles.compareButton}
          >
            Compare
          </CustomButton>
        </div>
      </div>
    </Modal>
  );
};

export default CompareModal;
